/**
 * Custom hook for streaming SBA document search via Server-Sent Events.
 *
 * Consumes the /api/sba/search/documents/stream SSE endpoint and
 * progressively shows status → intent → documents as results arrive.
 *
 * Follows the same SSE parsing pattern as useStreamingSearch.ts.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { auth } from '../config/firebase';
import { getImpersonationHeaders } from '../services/impersonationHeaders';
import type {
  SBADocumentResult,
  SBADocumentSearchConcept,
  SBADocumentSearchMetadata,
} from '../types/sba';
import type { SBADocType } from '../types';
import { canonicalizeSbaDocTypes } from '../constants/filterConstants';

const API_URL =
  import.meta.env['VITE_STREAMING_API_URL'] ||
  import.meta.env['VITE_API_URL'] ||
  'https://us-central1-label-doctor-f255e.cloudfunctions.net/apiProxy';

export const SBA_DOCUMENT_PAGE_SIZE = 50;
export const SBA_DOCUMENT_MAX_RESULTS = 1000;

interface SbaDocumentSearchState {
  /** Ranked SBA documents — populated when 'documents' event arrives */
  documents: SBADocumentResult[];
  /** True while the SSE stream is active */
  isLoading: boolean;
  /** Error if the stream or connection failed */
  error: Error | null;
  /** Total logical documents available across result pages */
  documentCount: number;
  /** Current status message from pipeline */
  statusMessage: string;
  /** Extracted concepts (populated on 'intent' event) */
  concepts: SBADocumentSearchConcept[];
  /** Final metadata (populated on 'done' event) */
  metadata: SBADocumentSearchMetadata | null;
}

interface SSEEvent {
  type: string;
  data: string;
}

type RollingEvidence = SBADocumentResult['evidence_sections'][number] & {
  document_id?: number;
};
type RollingDocument = Omit<SBADocumentResult, 'evidence_sections'> & {
  logical_document_key?: string;
  evidence_sections: RollingEvidence[];
};

/**
 * Normalize the two additive identity fields needed by the logical-document
 * UI during a mixed frontend/backend rollout. The public SBA result types stay
 * unchanged until the result-card task lands; the properties are retained on
 * the runtime object for that consumer.
 */
export function normalizeSbaDocumentResult(
  document: SBADocumentResult,
): SBADocumentResult {
  const rollingDocument = document as RollingDocument;
  return {
    ...rollingDocument,
    logical_document_key:
      rollingDocument.logical_document_key || `physical:${document.document_id}`,
    evidence_sections: rollingDocument.evidence_sections.map((evidence) => ({
      ...evidence,
      document_id: evidence.document_id ?? document.document_id,
    })),
  } as SBADocumentResult;
}

function parseSSEBuffer(buffer: string): { events: SSEEvent[]; remaining: string } {
  const events: SSEEvent[] = [];
  const parts = buffer.split('\n\n');
  const remaining = parts.pop() || '';

  for (const part of parts) {
    if (!part.trim()) continue;

    let eventType = 'message';
    let eventData = '';

    for (const line of part.split('\n')) {
      if (line.startsWith('event: ')) {
        eventType = line.slice(7).trim();
      } else if (line.startsWith('data: ')) {
        eventData = line.slice(6);
      }
    }

    if (eventData) {
      events.push({ type: eventType, data: eventData });
    }
  }

  return { events, remaining };
}

export function useSbaDocumentSearch(
  query: string,
  enabled: boolean = true,
  docTypes?: readonly SBADocType[],
  page: number = 1,
  pageSize: number = SBA_DOCUMENT_PAGE_SIZE,
  /** 'US_FDA' selects the Drugs@FDA path; omitted -> body identical to before. */
  region?: 'US_FDA',
): SbaDocumentSearchState {
  const [documents, setDocuments] = useState<SBADocumentResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [documentCount, setDocumentCount] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const [concepts, setConcepts] = useState<SBADocumentSearchConcept[]>([]);
  const [metadata, setMetadata] = useState<SBADocumentSearchMetadata | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  // One canonical scalar dependency prevents a reorder/new-array identity from
  // aborting and restarting an equivalent request.
  const docTypesKey = canonicalizeSbaDocTypes(docTypes).join(',');

  const startStream = useCallback(async (
    searchQuery: string,
    canonicalDocTypes: readonly SBADocType[],
    requestedPage: number,
    requestedPageSize: number,
    requestedRegion?: 'US_FDA',
  ) => {
    // Abort any previous stream
    if (abortRef.current) {
      abortRef.current.abort();
    }

    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    setError(null);
    setDocuments([]);
    setDocumentCount(0);
    setStatusMessage('Starting search...');
    setConcepts([]);
    setMetadata(null);

    try {
      const currentUser = auth.currentUser;
      const token = currentUser ? await currentUser.getIdToken(false) : null;
      if (controller.signal.aborted) return;

      const body = {
        query: searchQuery,
        // Retrieval is already bounded to at most 1,000 diversified logical
        // candidates. Retain that complete bounded set, then page the cards.
        top_k: SBA_DOCUMENT_MAX_RESULTS,
        page: requestedPage,
        page_size: requestedPageSize,
        doc_types: canonicalDocTypes,
        ...(requestedRegion ? { region: requestedRegion } : {}),
      };

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      Object.assign(headers, getImpersonationHeaders());

      const response = await fetch(
        `${API_URL}/sba/search/documents/stream`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(body),
          signal: controller.signal,
        },
      );

      if (!response.ok) {
        throw new Error(`Stream request failed: ${response.status} ${response.statusText}`);
      }

      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      const processEvents = (events: SSEEvent[]) => {
        if (controller.signal.aborted) return;
        for (const event of events) {
          try {
            const parsed = JSON.parse(event.data);

            switch (event.type) {
              case 'status':
                setStatusMessage(parsed.message || '');
                break;

              case 'intent':
                setConcepts(parsed.concepts || []);
                setStatusMessage(
                  `Found ${parsed.buckets} concept channels, searching...`
                );
                break;

              case 'documents': {
                const docs = (parsed as SBADocumentResult[]).map(
                  normalizeSbaDocumentResult,
                );
                // Sort by score descending
                const sorted = [...docs].sort((a, b) => b.score - a.score);
                setDocuments(sorted);
                setDocumentCount(sorted.length);
                setStatusMessage(`Found ${sorted.length} documents`);
                break;
              }

              case 'done':
                setMetadata(parsed.metadata || null);
                if (parsed.documents) {
                  const finalDocs = (parsed.documents as SBADocumentResult[])
                    .map(normalizeSbaDocumentResult)
                    .sort((a, b) => b.score - a.score);
                  setDocuments(finalDocs);
                  setDocumentCount(
                    typeof parsed.total_documents === 'number'
                      ? parsed.total_documents
                      : finalDocs.length,
                  );
                }
                setIsLoading(false);
                setStatusMessage('');
                break;

              case 'error':
                setError(new Error(parsed.message || 'Stream error'));
                setIsLoading(false);
                setStatusMessage('');
                break;

              default:
                break;
            }
          } catch (parseErr) {
            console.warn('[SBA-DOC-SSE] Failed to parse event:', event.type, parseErr);
          }
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const { events, remaining } = parseSSEBuffer(buffer);
        buffer = remaining;
        processEvents(events);
      }

      // Flush remaining buffer
      if (buffer.trim()) {
        const { events } = parseSSEBuffer(buffer + '\n\n');
        processEvents(events);
      }

      if (!controller.signal.aborted) {
        setIsLoading(false);
      }
    } catch (err: unknown) {
      if (
        controller.signal.aborted
        || (err instanceof Error && err.name === 'AbortError')
      ) {
        return;
      }
      console.error('[SBA-DOC-SSE] Stream error:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const canonicalDocTypes = docTypesKey
      ? (docTypesKey.split(',') as SBADocType[])
      : [];
    if (!enabled || !query.trim() || canonicalDocTypes.length === 0) {
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      setDocuments([]);
      setIsLoading(false);
      setError(null);
      setDocumentCount(0);
      setStatusMessage('');
      setConcepts([]);
      setMetadata(null);
      return;
    }

    startStream(query, canonicalDocTypes, page, pageSize, region);

    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, enabled, docTypesKey, page, pageSize, region, startStream]);

  return {
    documents,
    isLoading,
    error,
    documentCount,
    statusMessage,
    concepts,
    metadata,
  };
}

export default useSbaDocumentSearch;
